import { Suspense, type PropsWithChildren, type ReactNode } from "react";
import {
  AnnotationHeart,
  ArrowLeft,
  Bell01,
  LogIn04,
  Settings01,
} from "@untitled-ui/icons-react";
import { Link } from "react-router";
import { Row, Spacer } from "@supply25/design/layout";
import { useUser, useWorkspace } from "@/context/index.ts";
import { useLogout } from "@/hooks/useLogout.ts";
import { Avatar } from "./Avatar.tsx";
import { Menu } from "./Menu.tsx";
import { SpinningLoadingIcon } from "./icons/SpinningLoadingIcon.tsx";
import Logo from "@/assets/s25-logo.svg?react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { listMyNotificationsQuery } from "@/queries.ts";
import { isFeatureFlagOn } from "@/service/featureFlags.ts";
import { Button } from "@supply25/design/button";
import { IconLink } from "@/components/Link.tsx";
import { cn } from "@/service/classname.ts";

type PageProps = PropsWithChildren<{
  back?: string;
  title?: ReactNode;
  h1?: boolean;
  actions?: ReactNode;
  scrollable?: boolean;
}>;

const layout = "grid grid-rows-[auto_minmax(0,1fr)] gap-4 p-4 lg:p-6";

export const Page = ({
  back,
  title,
  h1 = false,
  actions,
  children,
  scrollable = true,
}: PageProps) => (
  <main className={cn(scrollable ? "h-full" : "h-screen", layout)}>
    <PageHeader back={back} title={title} h1={h1} actions={actions} />
    {children}
  </main>
);

// For large canvases like the risk map: content runs edge to edge and the header floats over it.
// The header's empty space lets pointer events through so the content stays usable underneath.
export const FullBleedPage = ({
  back,
  title,
  h1 = false,
  actions,
  children,
}: Omit<PageProps, "scrollable">) => (
  <main className="relative h-screen">
    <div className="absolute inset-0">{children}</div>
    <PageHeader
      back={back}
      title={title}
      h1={h1}
      actions={actions}
      className="pointer-events-none absolute inset-x-0 top-0 px-8 py-4 *:pointer-events-auto"
    />
  </main>
);

const PageHeader = ({
  back,
  title,
  h1,
  actions,
  className,
}: Omit<PageProps, "scrollable" | "children"> & { className?: string }) => {
  const Heading = h1 ? "h1" : "div";

  return (
    <Row as="header" align="center" className={className}>
      <Row gap="sm" align="center">
        {back && (
          <IconLink to={back} icon={<ArrowLeft />} label="Back" theme="text" />
        )}
        {title && <Heading className="subtitle">{title}</Heading>}
      </Row>
      <Spacer />
      <Row gap="sm">
        {actions}
        {isFeatureFlagOn("newDashboard") && (
          <Suspense fallback={<BellLink />}>
            <NotificationMenu />
          </Suspense>
        )}
        <UserMenu />
      </Row>
    </Row>
  );
};

const NotificationMenu = () => {
  const { organisation } = useWorkspace();
  const { data: notifications } = useSuspenseQuery({
    ...listMyNotificationsQuery(organisation.id),
    refetchInterval: 30000,
  });
  const haveUnread = notifications.some((notification) => !notification.read);
  return (
    <span className="relative">
      <BellLink />
      {haveUnread && (
        <span className="absolute right-1 top-1 size-2 rounded-full bg-red-500" />
      )}
    </span>
  );
};

const BellLink = () => {
  const { workspace } = useWorkspace();
  return (
    <IconLink
      label="See all notifications"
      icon={<Bell01 />}
      to={`/w/${workspace.id}/notifications`}
      className="Button"
      theme="secondary"
      size="sm"
    />
  );
};

const UserMenu = () => {
  const user = useUser();
  const logout = useLogout();
  const { workspace } = useWorkspace();
  const id = "user-menu";
  return (
    <>
      <Button
        popoverTarget={id}
        label="Account options"
        icon={<Avatar name={user.name} />}
        theme="secondary"
        className="p-0"
      />

      <Menu id={id} className="max-w-sm">
        <div className="grid grid-cols-[auto_minmax(0,1fr)] gap-2 p-2">
          <Avatar name={user.name} />
          <div>
            <div>{user.name}</div>
            <div className="truncate text-sm">{user.email}</div>
          </div>
        </div>
        <hr />
        <Link to={`/w/${workspace.id}/settings`} className="mt-1">
          <Settings01 /> Settings
        </Link>
        <button onClick={() => logout.mutate()}>
          {logout.isPending ? <SpinningLoadingIcon /> : <LogIn04 />} Log out
        </button>
        <a target="_blank" href="https://forms.gle/9FyGujuc6pSuVy3E7">
          <AnnotationHeart /> Feedback
        </a>
      </Menu>
    </>
  );
};

export const PublicPage = ({
  back,
  title,
  h1 = false,
  children,
}: PageProps) => {
  const Heading = h1 ? "h1" : "span";
  return (
    <main className={layout}>
      <Row align="center" as="header">
        <Row gap="sm" align="center">
          {back && <IconLink to={back} label="Back" icon={<ArrowLeft />} />}
          <Heading className="subtitle">{title}</Heading>
        </Row>
        <Spacer />
        <Logo />
      </Row>
      {children}
    </main>
  );
};
